<template>
  <div class="flex justify-end flex-wrap gap-[8px]">
    <div
      class="flex items-center rounded-[8px] h-[43.5px] overflow-hidden !border border-[#e2e2e2] has-[:focus]:border-[#f95031] w-full max-w-[320px]"
    >
      <i
        class="k-search-normal-1 ml-[12px] text-[24px] cursor-pointer"
        @click="$refs.searchMember.focus()"
      />
      <b-form-input
        ref="searchMember"
        v-model="keyword"
        type="search"
        placeholder="Cari nama, email atau no HP"
        class="border-0 shadow-none !text-[14px]"
        size="lg"
        :debounce="DEBOUNCED_CHANGE_TIMEOUT"
        @update="triggerFilterChange"
      />
    </div>
    <nested-dropdown
      ref="filterDropdown"
      right
      size="sm"
      toggle-class="text-decoration-none toggle-custom button-primary rounded-lg py-[7px] px-1"
      @submenu-active="closeDatePicker"
      @hidden="closeDatePicker"
    >
      <template #toggle>
        <i class="k-filter-search text-[24px]" />
      </template>
      <template #header>
        <div class="p-[16px] pt-[9px] border-b-[1px] border-[#E2E2E2] text-[#000000] font-semibold">
          Filter
        </div>
      </template>
      <nested-dropdown-submenu
        ref="categorySubmenu"
        label="Kategori"
        menu-class="!min-w-[200px]"
        toggle-class="!rounded-none text-left !text-[#000000] !font-medium !px-[16px] !py-[12px]"
      >
        <b-dropdown-form style="width: 200px">
          <div
            v-if="isCategoryLoading"
            class="text-center py-2 text-[12px] text-gray-500"
          >
            Memuat data...
          </div>
          <div
            v-else-if="isCategoryError"
            class="flex flex-col items-center justify-center gap-[8px] py-2"
          >
            <span class="text-[12px] text-red-500 text-center">Gagal memuat data</span>
            <b-button
              size="sm"
              variant="outline-primary"
              class="w-full !text-[12px]"
              @click.prevent="fetchCategoryOptions"
            >
              Coba Lagi
            </b-button>
          </div>
          <b-form-checkbox-group
            v-else
            v-model="selectedCategory"
            stacked
            class="text-6 [&_.custom-checkbox:not(:last-child)]:mb-[20px]"
            :options="categoryOptions"
            @input="debouncedCategoryChange"
          />
        </b-dropdown-form>
      </nested-dropdown-submenu>
      <date-picker
        ref="datePicker"
        v-model="period"
        :date-format="createDatePickerRestrictor()"
        :ranges="datePickerRange"
        :container-class="datepickerContainerClass"
        control-container-class="!rounded-none"
        @toggle="handleDatePickerToggle"
        @input="triggerFilterChange"
      >
        <template #default>
          <div
            class="d-flex justify-between gap-[14px] items-center w-100 !px-[16px] !py-[12px] !rounded-none"
            @click.stop="handleTanggalClick"
          >
            Tanggal
          </div>
        </template>
        <template #footer="{ data: { rangeText, clickCancel, clickApply } }">
          <div class="flex justify-end items-center gap-[10px] py-[12px] px-[23px] border-t-[1px] border-[#E2E2E2]">
            <span class="text-[#828282] text-[12px]">{{ rangeText }}</span>
            <b-button
              variant="outline-primary"
              size="sm"
              @click="clickCancel"
            >
              Kembali
            </b-button>
            <b-button
              variant="primary"
              size="sm"
              @click="clickApply"
            >
              Submit
            </b-button>
          </div>
        </template>
      </date-picker>
      <b-button
        variant="ghost"
        size="sm"
        class="!text-[#E31A1A] !font-normal text-[12px] !px-[16px] !py-[7px] -mb-[7px] !rounded-none !rounded-b-[4px] !w-full !border-none text-left hover:bg-[#FCD4BE]"
        @click="resetFilter"
      >
        Reset Filter
      </b-button>
    </nested-dropdown>
  </div>
</template>

<script>
import { NestedDropdown, NestedDropdownSubmenu } from '@core/components/nested-dropdown'
import { today } from '@/store/helpers'
import DatePicker from '@/views/pages/kompack/DatePicker.vue'
import { createDatePickerRestrictor } from '@/libs/filterDate'
import { datePickerRange } from '../config'
import { parseDate } from '../utils'

export default {
  name: 'FilterComponent',
  components: {
    NestedDropdown,
    NestedDropdownSubmenu,
    DatePicker,
  },
  data() {
    return {
      keyword: '',
      selectedCategory: [],
      period: {
        startDate: this.$route.query.start_date ? new Date(this.$route.query.start_date) : null,
        endDate: this.$route.query.end_date ? new Date(this.$route.query.end_date) : null,
      },
      periodOnSelection: false,
      categoryOptions: [],
      isCategoryLoading: false,
      isCategoryError: false,
    }
  },
  computed: {
    datepickerContainerClass() {
      const baseClass = 'border-none !w-full cursor-pointer !text-[#000000] !font-medium text-[12px] hover:bg-[#FCD4BE]'
      if (this.periodOnSelection) {
        return `${baseClass} !bg-[#FCD4BE]`
      }
      return baseClass
    },
  },
  created() {
    this.DEBOUNCED_CHANGE_TIMEOUT = 500
    this.debouncedCategoryChange = _.debounce(this.triggerFilterChange, this.DEBOUNCED_CHANGE_TIMEOUT)
    this.datePickerRange = datePickerRange(today)
  },
  beforeDestroy() {
    if (this.debouncedCategoryChange) {
      this.debouncedCategoryChange.cancel()
    }
  },
  mounted() {
    this.syncFromRoute()
    this.fetchCategoryOptions()
  },
  methods: {
    async fetchCategoryOptions() {
      this.isCategoryLoading = true
      this.isCategoryError = false
      try {
        const response = await this.$http_new.get('/komclass/api/v1/komlabs/campaigns/options', {
          params: { option_type: 'product_category' },
        })
        this.categoryOptions = response.data.data.map(({ option_label }) => ({
          value: option_label,
          text: option_label,
        }))
      } catch (error) {
        this.isCategoryError = true
        this.$toast({ message: 'Gagal memuat data kategori' })
      } finally {
        this.isCategoryLoading = false
      }
    },
    /**
     * Sinkronkan state filter dari query string browser
     */
    syncFromRoute() {
      const {
        keyword, category, start_date: startDate, end_date: endDate,
      } = this.$route.query
      this.keyword = keyword || ''
      this.selectedCategory = category ? category.split(',') : []
      if (startDate) this.period.startDate = new Date(startDate)
      if (endDate) this.period.endDate = new Date(endDate)
    },
    createDatePickerRestrictor,
    /**
     * Tutup submenu kategori saat date picker dibuka
     * @param {boolean} open
     */
    handleDatePickerToggle(open) {
      this.periodOnSelection = open
      if (open) {
        const { categorySubmenu } = this.$refs
        if (categorySubmenu) categorySubmenu.close()
      }
    },
    /** Buka/tutup date picker dengan koordinasi submenu kategori */
    handleTanggalClick() {
      const { categorySubmenu, datePicker } = this.$refs
      if (categorySubmenu && categorySubmenu.isShowing) {
        categorySubmenu.close()
        this.$nextTick(() => {
          if (datePicker) datePicker.openPicker()
        })
        return
      }
      if (datePicker) datePicker.togglePicker()
    },
    /** Tutup date picker */
    closeDatePicker() {
      const { datePicker } = this.$refs
      if (datePicker) datePicker.closePicker()
    },
    /**
     * Reset filter kategori dan periode, tutup dropdown
     */
    resetFilter() {
      this.selectedCategory = []
      this.period = {
        startDate: null,
        endDate: null,
      }
      this.periodOnSelection = false
      this.closeDatePicker()
      if (this.debouncedCategoryChange) {
        this.debouncedCategoryChange.cancel()
      }

      this.triggerFilterChange()

      const { filterDropdown } = this.$refs
      if (filterDropdown) filterDropdown.close()
    },
    /** Reset keyword dan seluruh filter */
    resetAll() {
      this.keyword = ''
      this.resetFilter()
    },
    /**
     * Sinkronkan URL dan emit perubahan filter ke parent
     */
    triggerFilterChange() {
      this.updateRouteParams()
      this.emitFilterData()
    },
    /**
     * Emit payload filter ke parent
     * @returns {{ keyword: string, category: string[], start_date: string, end_date: string }}
     */
    emitFilterData() {
      const payload = {
        keyword: this.keyword,
        category: [...this.selectedCategory],
        start_date: parseDate(this.period.startDate),
        end_date: parseDate(this.period.endDate),
      }
      this.$emit('filter-change', payload)
      return payload
    },
    getRouteQuery() {
      const query = {
        start_date: parseDate(this.period.startDate),
        end_date: parseDate(this.period.endDate),
      }
      const { tab } = this.$route.query
      if (tab) query.tab = tab
      if (this.keyword) query.keyword = this.keyword
      if (this.selectedCategory.length) query.category = this.selectedCategory.join(',')
      return query
    },
    /** Perbarui query string browser agar sesuai state filter */
    updateRouteParams() {
      const query = this.getRouteQuery()
      if (_.isEqual(query, this.$route.query)) return
      this.$router.replace({ query }).catch(() => { })
    },
  },
}
</script>
